import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Plus, Trophy, Users, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import {
  CHESS_CLOCK_PRESETS,
  type TournamentKind,
  type TournamentStatus,
} from '@/types/qchess';
import {
  useCreateTournamentMutation,
  useListTournamentsQuery,
} from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { cn } from '@/lib/cn';

type FilterValue = 'all' | TournamentStatus;
const FILTERS: readonly FilterValue[] = ['all', 'created', 'started', 'finished'];

/**
 * Chess tournaments — filterable list + a "create tournament" form (kind,
 * clock preset, name, rated). Each row links to the tournament detail.
 */
export function ChessTournaments() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [filter, setFilter] = useState<FilterValue>('all');
  const [formOpen, setFormOpen] = useState(false);

  const listQuery = useListTournamentsQuery(
    filter === 'all' ? undefined : { status: filter },
  );
  const tournaments = listQuery.data?.tournaments ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Trophy aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('tournaments.title')}</h1>
        </div>
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            setFormOpen((v) => !v);
          }}
          className="flex items-center gap-1 rounded-lg bg-brand px-3 py-2 text-xs font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          {formOpen ? (
            <X aria-hidden className="h-4 w-4" />
          ) : (
            <Plus aria-hidden className="h-4 w-4" />
          )}
          {t('tournaments.create')}
        </button>
      </header>

      {formOpen && <CreateTournamentForm onCreated={() => setFormOpen(false)} />}

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => {
              haptics.selection();
              setFilter(f);
            }}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-opacity active:opacity-80',
              filter === f
                ? 'bg-brand text-brand-fg'
                : 'bg-bg-secondary text-fg-muted',
            )}
          >
            {t(`tournaments.filter.${f}`)}
          </button>
        ))}
      </div>

      {listQuery.isLoading ? (
        <Loader />
      ) : listQuery.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qchessErrorKey(classifyQchessError(listQuery.error)))}
        </p>
      ) : tournaments.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">
          {t('tournaments.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tournaments.map((tour) => (
            <li key={tour.lichessId}>
              <Link
                to={`/chess/tournaments/${encodeURIComponent(tour.lichessId)}`}
                onClick={() => haptics.impact('light')}
                className="flex items-center gap-3 rounded-xl bg-bg-secondary p-3 transition-opacity active:opacity-80"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-fg">{tour.name}</p>
                    <ChessStatusBadge status={tour.status} />
                  </div>
                  <p className="mt-0.5 flex items-center gap-2 text-[11px] text-fg-muted">
                    <span className="uppercase">{tour.kind}</span>
                    <span aria-hidden>·</span>
                    <span className="flex items-center gap-1">
                      <Users aria-hidden className="h-3 w-3" />
                      {t('tournaments.players', { count: tour.nbPlayers })}
                    </span>
                  </p>
                </div>
                <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Create form ──────────────────────────────────────────────────────── */

function CreateTournamentForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<TournamentKind>('arena');
  const [clockId, setClockId] = useState(CHESS_CLOCK_PRESETS[1].id); // blitz default
  const [rated, setRated] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createTournament, { isLoading }] = useCreateTournamentMutation();

  const preset = useMemo(
    () => CHESS_CLOCK_PRESETS.find((p) => p.id === clockId) ?? CHESS_CLOCK_PRESETS[1],
    [clockId],
  );

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (isLoading) return;
    setError(null);
    try {
      await createTournament({
        kind,
        name: name.trim() || undefined,
        clockLimitSeconds: preset.clockLimitSeconds,
        clockIncrementSeconds: preset.clockIncrementSeconds,
        rated,
      }).unwrap();
      haptics.notification('success');
      onCreated();
    } catch (err) {
      haptics.notification('error');
      setError(t(qchessErrorKey(classifyQchessError(err))));
    }
  };

  // Drive submission from the Telegram MainButton (consistent with DuelLink).
  useMainButton({
    text: t('tournaments.form.submit'),
    enabled: !isLoading,
    loading: isLoading,
    onClick: () => void submit(),
  });

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-xl bg-bg-secondary p-4"
    >
      <h2 className="text-sm font-bold text-fg">{t('tournaments.form.title')}</h2>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('tournaments.form.name')}
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('tournaments.form.namePlaceholder')}
          maxLength={60}
          className="rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
        />
      </label>

      {/* Kind */}
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('tournaments.form.kind')}
        </legend>
        <div className="flex gap-2">
          {(['arena', 'swiss'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                haptics.selection();
                setKind(k);
              }}
              className={cn(
                'flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide transition-opacity active:opacity-80',
                kind === k ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {t(`tournaments.form.kind${k === 'arena' ? 'Arena' : 'Swiss'}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Clock presets */}
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('tournaments.form.clock')}
        </legend>
        <div className="flex gap-2">
          {CHESS_CLOCK_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                haptics.selection();
                setClockId(p.id);
              }}
              className={cn(
                'flex-1 rounded-lg px-2 py-2 text-[11px] font-bold transition-opacity active:opacity-80',
                clockId === p.id ? 'bg-brand text-brand-fg' : 'bg-bg-tertiary text-fg-muted',
              )}
            >
              {t(`clock.${p.id}`)}
            </button>
          ))}
        </div>
      </fieldset>

      {/* Rated toggle */}
      <label className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {t('tournaments.form.rated')}
        </span>
        <input
          type="checkbox"
          checked={rated}
          onChange={(e) => {
            haptics.selection();
            setRated(e.target.checked);
          }}
          className="h-5 w-5 accent-[var(--color-brand)]"
        />
      </label>

      {error && (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
